import { useState, useEffect } from 'react';
import { IPresenterProps } from './types';
import * as S from './styles';
import GoogleMapReact from 'google-map-react';
import mapStyles from './mapStyles';
import { IBusiness } from '../../state/api/types';

const MapPresenter = (props: IPresenterProps) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    const defaultCenter = { lat: -34.90709220993874, lng: -56.20464384943513 };

    // Store map instance for reactive updates
    const [mapInstance, setMapInstance] = useState<{ map: any; maps: any } | null>(null);

    // Filter businesses with valid coordinates
    const businessesWithCoordinates = props.businesses.filter((business: IBusiness) => {
        const lat = Number(business.lat);
        const lng = Number(business.lng);
        return !isNaN(lat) && !isNaN(lng) &&
               business.lat !== undefined && business.lat !== null &&
               business.lng !== undefined && business.lng !== null;
    });

    // Reactive effect: update bounds when businesses or map instance changes
    useEffect(() => {
        if (!mapInstance || businessesWithCoordinates.length === 0) return;

        const { map, maps } = mapInstance;

        // Single business: center directly with comfortable zoom
        if (businessesWithCoordinates.length === 1) {
            const b = businessesWithCoordinates[0];
            map.setCenter({ lat: Number(b.lat), lng: Number(b.lng) });
            map.setZoom(13);
            return;
        }

        // Multiple businesses: fit bounds with generous padding for marker labels
        const bounds = new maps.LatLngBounds();
        businessesWithCoordinates.forEach(business => {
            bounds.extend({ lat: Number(business.lat), lng: Number(business.lng) });
        });

        // Large horizontal padding to account for marker label text width
        map.fitBounds(bounds, {
            top: 80,
            right: 150,
            bottom: 80,
            left: 150
        });

        // Clamp zoom to prevent over-zooming
        const listener = maps.event.addListener(map, 'idle', () => {
            if (map.getZoom() > 14) {
                map.setZoom(14);
            }
            maps.event.removeListener(listener);
        });
    }, [businessesWithCoordinates, mapInstance]);

    const handleApiLoaded = ({ map, maps }: { map: any; maps: any }) => {
        setMapInstance({ map, maps });
    };

    return (
        <S.Container>
            <GoogleMapReact
                bootstrapURLKeys={{ key: apiKey || '' }}
                defaultCenter={defaultCenter}
                defaultZoom={13}
                margin={[50, 50, 50, 50]}
                options={{ disableDefaultUI: true, zoomControl: true, styles: mapStyles }}
                onChildClick={(child: string) => {
                    props.handleBusinessClicked(child);
                }}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={handleApiLoaded}
            >
                {businessesWithCoordinates.map((business: IBusiness, i) => (
                    <S.BusinessMarkerContainer
                        lat={Number(business.lat)}
                        lng={Number(business.lng)}
                        key={i}
                    >
                        <S.Marker>
                            <p>{business.name}</p>
                        </S.Marker>
                    </S.BusinessMarkerContainer>
                ))}
            </GoogleMapReact>
        </S.Container>
    );
};

export default MapPresenter;
